import React, { useMemo, useState } from "react";
import axios from "axios";
import { AlertCircle, Building2, Check, Loader2, MapPin, Search, ShieldCheck } from "lucide-react";
import { API } from "../../apiBase";

const A = `${API}/mss-commande/annuaire`;
const FORMAT = {
  SIREN: [/^\d{9}$/, "9 chiffres"],
  FINESS: [/^[0-9A-Z]{9}$/, "9 caractères"],
  SIRET: [/^\d{14}$/, "14 chiffres"],
};
const SUPPORT = process.env.REACT_APP_OMV_SUPPORT_EMAIL || "contact@metaconnect.fr";
const detail = (e, defaut) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : defaut);

/**
 * Création d'une structure vérifiée dans l'Annuaire santé (même logique que le lien OhMyVue) :
 * 1. entité juridique par SIREN ou FINESS, puis choix de l'établissement dans la liste de l'annuaire ;
 * 2. ou directement le SIRET / FINESS de l'établissement.
 * Le nom et l'adresse sont repris de l'annuaire ; une structure introuvable ou fermée est refusée.
 */
export default function StructureAnnuaireForm({ onCancel, onCreated, testid = "struct-annu" }) {
  const [mode, setMode] = useState("ej");
  const [typeId, setTypeId] = useState("SIREN");
  const [ident, setIdent] = useState("");
  const [etat, setEtat] = useState({ etat: "" });
  const [ej, setEj] = useState(null);
  const [egs, setEgs] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [choix, setChoix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const valeur = ident.replace(/\s/g, "").toUpperCase();
  const [re, lib] = FORMAT[typeId];
  const formatOk = re.test(valeur);

  const changerMode = (m) => {
    setMode(m); setTypeId(m === "ej" ? "SIREN" : "SIRET"); setIdent("");
    setEtat({ etat: "" }); setEj(null); setEgs([]); setChoix(null); setErr("");
  };

  const rechercher = async () => {
    setErr(""); setEj(null); setEgs([]); setChoix(null); setFiltre("");
    setEtat({ etat: "encours" });
    try {
      if (mode === "ej") {
        const { data } = await axios.get(`${A}/entite-juridique`, { params: { type: typeId, id: valeur }, timeout: 20000 });
        if (!data.ouverte) { setEtat({ etat: "erreur", msg: "Cette entité juridique est fermée dans l'Annuaire santé." }); return; }
        setEj(data);
        const r = await axios.get(`${A}/etablissements`, { params: { ej: (data.fhir_ids || [data.fhir_id]).join(",") }, timeout: 20000 });
        const liste = (r.data.etablissements || []).filter((x) => x.id);
        setEgs(liste);
        setEtat({ etat: liste.length ? "ok" : "vide" });
      } else {
        const { data } = await axios.get(`${A}/etablissement`, { params: { type: typeId, id: valeur }, timeout: 20000 });
        const eg = data.etablissement;
        if (!eg.ouverte) { setEtat({ etat: "erreur", msg: "Cet établissement est fermé dans l'Annuaire santé." }); return; }
        setEj(data.entite_juridique);
        setEgs([eg]); setChoix(eg);
        setEtat({ etat: "ok" });
      }
    } catch (e) {
      const st = e?.response?.status;
      setEtat({
        etat: "erreur",
        msg: st === 404 ? `${typeId} ${valeur} introuvable dans l'Annuaire santé. Vérifiez le numéro.`
          : st === 503 ? "L'Annuaire santé ne répond pas pour le moment. Réessayez dans quelques minutes."
            : detail(e, "Recherche impossible pour le moment."),
      });
    }
  };

  const visibles = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return f ? egs.filter((x) => `${x.nom} ${x.adresse} ${x.id}`.toLowerCase().includes(f)) : egs;
  }, [egs, filtre]);

  const creer = async () => {
    if (!choix) return;
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/pro/structures`, {
        structure_id_type: choix.type_id, structure_id: choix.id, structure_name: choix.nom || "",
      });
      onCreated(data);
    } catch (e) {
      setErr(detail(e, "Création impossible."));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4 lg:p-5 bg-[#F8FAFC] grid gap-4" data-testid={`${testid}-form`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] uppercase tracking-[0.16em] text-[#0369A1]">Nouvelle structure</div>
        <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700"><ShieldCheck size={13} /> Vérifiée dans l'Annuaire santé</span>
      </div>

      <div className="inline-flex p-1 rounded-full bg-white border border-slate-200 self-start flex-wrap" role="group" aria-label="Mode de recherche">
        {[["ej", "Par l'entité juridique (SIREN ou FINESS)"], ["eg", "Par le SIRET ou FINESS de l'établissement"]].map(([m, l]) => (
          <button key={m} type="button" aria-pressed={mode === m} onClick={() => changerMode(m)} data-testid={`${testid}-mode-${m}`}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium transition ${mode === m ? "bg-[#0F2B5B] text-white" : "text-[#475569] hover:text-[#0F2B5B]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-[auto_1fr_auto] gap-2 items-end">
        <label className="grid gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#0369A1]">Type</span>
          <select className="mc-input" value={typeId} onChange={(e) => { setTypeId(e.target.value); setEtat({ etat: "" }); }} data-testid={`${testid}-type`}>
            {(mode === "ej" ? ["SIREN", "FINESS"] : ["SIRET", "FINESS"]).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#0369A1]">
            {mode === "ej" ? `${typeId} de l'entité juridique` : `${typeId} de l'établissement`} ({lib})
          </span>
          <input className="mc-input font-mono" value={ident} inputMode={typeId === "FINESS" ? "text" : "numeric"}
            onChange={(e) => { setIdent(e.target.value.replace(/[^0-9A-Za-z\s]/g, "").slice(0, 17)); setEtat({ etat: "" }); }}
            onKeyDown={(e) => { if (e.key === "Enter" && formatOk) rechercher(); }}
            placeholder={typeId === "SIRET" ? "00000000000000" : "000000000"} data-testid={`${testid}-id`} />
        </label>
        <button type="button" onClick={rechercher} disabled={!formatOk || etat.etat === "encours"} data-testid={`${testid}-search`}
          className="btn-primary px-4 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
          {etat.etat === "encours" ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Rechercher
        </button>
      </div>

      {etat.etat === "erreur" && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700 flex gap-2" role="alert">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{etat.msg} Votre structure est récente ou mal référencée ? Écrivez-nous à{" "}
            <a className="underline" href={`mailto:${SUPPORT}?subject=${encodeURIComponent(`Structure introuvable ${typeId} ${valeur}`)}`}>{SUPPORT}</a>.</span>
        </div>
      )}

      {ej && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-900">
          <span className="font-medium">Entité juridique : {ej.nom}</span>
          <span className="font-mono text-emerald-800"> · {ej.type_id} {ej.id}</span>
        </div>
      )}

      {etat.etat === "vide" && (
        <p className="text-[12.5px] text-[#475569]">Aucun établissement rattaché dans l'Annuaire santé : recherchez directement le SIRET ou le FINESS de l'établissement.</p>
      )}

      {mode === "ej" && egs.length > 0 && (
        <div className="grid gap-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[12.5px] text-[#0F2B5B] font-medium">{egs.length} établissement{egs.length > 1 ? "s" : ""} : choisissez celui qui portera la boîte</span>
            {egs.length > 6 && (
              <input className="mc-input py-1.5 text-[12.5px] w-60 max-sm:w-full" value={filtre} onChange={(e) => setFiltre(e.target.value)}
                placeholder="Filtrer par nom, ville, numéro" aria-label="Filtrer les établissements" data-testid={`${testid}-filtre`} />
            )}
          </div>
          <div className="grid gap-1.5 max-h-80 overflow-y-auto pr-1" data-testid={`${testid}-liste`}>
            {visibles.map((x) => {
              const sel = choix && choix.type_id === x.type_id && choix.id === x.id;
              return (
                <button key={`${x.type_id}-${x.id}`} type="button" disabled={!x.ouverte} onClick={() => setChoix(x)}
                  className={`text-left rounded-lg border px-3 py-2.5 flex items-start gap-3 transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    sel ? "border-[#0F2B5B] bg-white ring-1 ring-[#0F2B5B]" : "border-slate-200 bg-white hover:border-sky-400"}`}>
                  <Building2 size={15} className="mt-0.5 text-[#0369A1] shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-[#0F2B5B]">{x.nom}</span>
                    <span className="block text-[11.5px] text-[#64748B]">
                      <span className="font-mono">{x.type_id} {x.id}</span>
                      {x.adresse && <> · <MapPin size={10} className="inline -mt-0.5" /> {x.adresse}</>}
                      {!x.ouverte && " · fermé"}
                    </span>
                  </span>
                  {sel && <Check size={16} className="text-[#0F2B5B] shrink-0" />}
                </button>
              );
            })}
            {!visibles.length && <p className="text-[12.5px] text-[#64748B] px-1">Aucun établissement ne correspond à « {filtre} ».</p>}
          </div>
        </div>
      )}

      {choix && (
        <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3 text-[12.5px] text-[#334155]" data-testid={`${testid}-choix`}>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#64748B] mb-1">Structure qui sera créée</div>
          <div className="text-[14px] font-semibold text-[#0F2B5B]">{choix.nom}</div>
          <div className="font-mono">{choix.type_id} {choix.id}</div>
          {choix.adresse && <div>{choix.adresse}</div>}
        </div>
      )}

      {err && <p className="text-[12.5px] text-red-600 inline-flex items-center gap-1" role="alert"><AlertCircle size={12} /> {err}</p>}

      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
        <button type="button" onClick={creer} disabled={!choix || busy} data-testid={`${testid}-submit`}
          className="btn-primary px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy && <Loader2 size={12} className="animate-spin" />}Créer la structure
        </button>
      </div>
    </div>
  );
}
